import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Tooltip } from '@mui/material';
import Tabs, { TabItem } from 'shared/components/Tabs';
import style from './candidateEducation.module.scss';
import EmptyBox from '../../../../../src/assets/noFile.svg';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import TypographyWithI18N from 'shared/components/TypographyWithI18N';
import {
  getHiringManagerDetails,
  axiosCandidateSkills,
  axiosCandidateEducation,
} from '../../stateManagement/api';
import {
  ICandidateSkillResponse,
  ICandidateSkillPayload,
  ICandidateWorkHistoryPayload,
  ICandidateWorkHistoryResponse,
} from 'modules/hiring/models/candidateProfile.model';
import { Canceler } from 'axios';
import moment from 'moment';
import {useTranslation } from 'react-i18next';

import { calculateEachTotalWorkExperience, formatWorkExperienceDuration } from '../../utilities/index';
import { axiosGetWorkHistoryDetails } from '../../stateManagement/api';
import LoaderComponent from '../LoaderComponent';
import { ICandidateEducationResponse } from '../../models/candidateProfile.model';

const NoDataBox: React.FC<{ text: string }> = ({ text }) => (
  <div className={style.noDataBox}>
    <div className={style.noDataGrid}>
      <div style={{ margin: 0, marginBottom: '6px' }}>
        <img src={EmptyBox} alt='emptyBox' />
      </div>
      <div style={{ margin: 0 }}>
        <Box data-testid='nodata-div'>
          <Typography
            className={`kfic2-bodyText ${style.noWorkHistoryAdded}`}
            sx={{ marginBottom: '1.5px' }}
          >
            {text}
          </Typography>
        </Box>
      </div>
    </div>
  </div>
);

interface ICandidateDetails {
  candidateId: number;
}
export const employmentType = [
  { value: 1, type: 'Full Time Employee' },
  { value: 2, type: 'Part Time Employee' },
  { value: 3, type: 'Contractor / Freelancer' },
  { value: 4, type: 'Unknown' },
];

const CandidateEducationPage: React.FC<ICandidateDetails> = ({
  candidateId,
}) => {
  const [currentTab, setCurrentTab] = useState<string>('0');
  const [candidateSkill, setCandidateSkill] = useState<any>();
  const [candidateEducation, setCandidateEducation] = useState<any>();
  const [nimbleOrgId, setnimbleOrgId] = useState<any>();
  const [workHistory, setWorkHistory] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(true);
  let candidateSkillcanceler: Canceler;
  const { t: translate } = useTranslation();

  useEffect(() => {
    const fetchHiringManagerDetails = async () => {
      try {
        const getOrgId = await getHiringManagerDetails();
        setnimbleOrgId(getOrgId?.nimbleOrgId);
      } catch (error) {
        console.error('Error fetching hiring manager details:', error);
      }
    };
    fetchHiringManagerDetails();
  }, [candidateId]);

  useEffect(() => {
    if (typeof nimbleOrgId === 'number' && typeof candidateId === 'number') {
      getCandidateSkills();
      getWorkHistory();
      getCandidateEducation();
    }
  }, [candidateId, nimbleOrgId]);

  const getCandidateSkills = async () => {
    const payload: ICandidateSkillPayload = {
      candidateId: candidateId,
      orgId: nimbleOrgId,
    };
    try {
      const response: ICandidateSkillResponse = await axiosCandidateSkills(
        payload,
        (cancel: Canceler) => (candidateSkillcanceler = cancel)
      );
      if (response?.total === 0) {
        setCandidateSkill([]);
      } else if (response?.total > 0) {
        setCandidateSkill(() => response?.skills);
      }
    } catch (err) {
      throw err;
    }
  };

  const getCandidateEducation = async () => {
    try {
      const response: ICandidateEducationResponse =
        await axiosCandidateEducation(candidateId, nimbleOrgId);
      if (response?.candidateCredentials?.length === 0) {
        setCandidateEducation([]);
      } else if (response?.candidateCredentials.length > 0) {
        setCandidateEducation(response?.candidateCredentials);
      }
    } catch (err) {
      throw err;
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  };

  const getWorkHistory = async () => {
    try {
      if (candidateId) {
        const payload: ICandidateWorkHistoryPayload = {
          candidateId: candidateId,
          orgId: nimbleOrgId,
        };
        const response: ICandidateWorkHistoryResponse =
          await axiosGetWorkHistoryDetails(
            payload,
            (cancel: Canceler) => (candidateSkillcanceler = cancel)
          );
        if (response?.statusCode === 200) {
          if (response?.workHistory?.length > 0) {
            response?.workHistory?.map((wh: any) => {
              wh.employmentTypeName = employmentType.find(
                (e: any) => e.value === wh.employmentType
              )?.type;
              return wh;
            });
            setWorkHistory(response.workHistory);
          } else {
            setWorkHistory([]);
          }
        } else {
          setWorkHistory([]);
        }
      }
    } catch (err: any) {
      throw new Error(`API Error: "${err}"`);
    } finally {
      setIsLoading(false);
    }
  };

  const renderEducationDetails = () => (
    <Grid
      container
      spacing={3}
      className={style.gridContainer}
      sx={{
        marginTop: '0 !important',
      }}
    >
      {isLoading ? (
        Array?.from({ length: 1 }).map((stage, i) => {
          return (
            <>
              <LoaderComponent stage={stage} i={i} />
            </>
          );
        })
      ) : candidateEducation?.length ? (
        candidateEducation.map((education: any, index: number) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            key={index}
            sx={{
              paddingTop: '0 !important',
            }}
          >
            <Box
              sx={{
                padding: '16px 0',
              }}
            >
              <Tooltip title={`${education?.instituteName}`}>
                <Typography className={style.instituteName}>
                  {education?.instituteName}
                </Typography>
              </Tooltip>

              <Tooltip title={`${education?.degreeCertification}`}>
                <Typography className={style.degreeCertification}>
                  {education?.degreeCertification}
                </Typography>
              </Tooltip>

              <Tooltip title={`Education Dates`}>
                <Typography className={style.educationDate}>
                  {education?.startDate && education?.endDate
                    ? `${new Date(
                        education?.startDate
                      ).getFullYear()} - ${new Date(
                        education?.endDate
                      ).getFullYear()}`
                    : education?.startDate
                    ? new Date(education?.startDate).getFullYear()
                    : education?.endDate
                    ? new Date(education?.endDate).getFullYear()
                    : ''}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>
        ))
      ) : (
        <NoDataBox text={translate('noEducationToDisplay')} />
      )}
    </Grid>
  );

  const renderSkills = () => (
    <Box className={`${style.gridContainer} ${style.gridSkillContainer}`}>
      {isLoading ? (
        Array?.from({ length: 1 }).map((stage, i) => {
          return (
            <>
              <LoaderComponent stage={stage} i={i} />
            </>
          );
        })
      ) : candidateSkill?.length ? (
        candidateSkill.map((skill: any, index: number) => (
          <Box key={index}>
            <Box className={style.skillContainer}>
              <Typography className={style.skillName}>
                {skill.skillName}
              </Typography>
            </Box>
          </Box>
        ))
      ) : (
        <NoDataBox text={translate('noSkillsToDisplay')} />
      )}
    </Box>
  );

  const renderWorkHistory = () => (
    <Grid
      container
      spacing={3}
      className={style.gridContainer}
      sx={{
        marginTop: '0 !important',
      }}
    >
      {isLoading ? (
        Array?.from({ length: 1 }).map((stage, i) => {
          return (
            <>
              <LoaderComponent key={`${stage}-${i}`} stage={stage} i={i} />
            </>
          );
        })
      ) : workHistory?.length > 0 ? (
        workHistory.map((item: any, index: any) => {
          const experiencePeriod = calculateEachTotalWorkExperience(item);
          const formattedExperience = formatWorkExperienceDuration(
            experiencePeriod.years,
            experiencePeriod.months,
            translate
          );
          return (
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              key={index}
              sx={{
                paddingTop: '0 !important',
              }}
            >
              <Box
                sx={{
                  padding: '16px 0',
                }}
              >
                <Typography className={style.instituteName}>
                  {item.jobTitle}
                </Typography>

                <Typography className={style.employmentType}>
                  {item.employmentTypeName}
                </Typography>

                <Typography className={style.employmentType}>
                  {item.EmployerName}
                </Typography>

                {item?.startDate !== null &&
                  (item?.endDate !== null ||
                    (item?.endDate === null && item?.IsCurrent === true)) && (
                    <Typography className={style.kfic2SecondaryHeader}>
                      <span>{moment(item?.startDate).format('MMMM YYYY')}</span>
                      {' - '}
               
                      {item?.IsCurrent === true ? (
                        <>
                          <span>{translate('current')}</span>
                        </>
                      ) : (
                        <span>{moment(item?.endDate).format('MMMM YYYY')}</span>
                      )}
                      {'  '}
                      <span>
                        {'  •  '} {formattedExperience}
                      </span>
                    </Typography>
                  )}
                {item?.jobLocation && (
                  <Typography className={style.kfic2SecondaryHeader}>
                    <span style={{ marginTop: '15px !important' }}>
                      <LocationOnOutlinedIcon sx={{ fontSize: 11 }} />
                    </span>
                    <span>{item?.jobLocation}</span>
                  </Typography>
                )}
              </Box>
            </Grid>
          );
        })
      ) : (
        <NoDataBox text={translate('noWorkHistoryToDisplay')} />
      )}
    </Grid>
  );

  const tabItems: TabItem[] = [
    {
      i18nKey: 'workHistory',
      component: <Box>{renderWorkHistory()}</Box>,
    },
    {
      i18nKey: 'skills',
      component: <Box>{renderSkills()}</Box>,
    },
    {
      i18nKey: 'education',
      component: <Box>{renderEducationDetails()}</Box>,
    },
  ];

  return (
    <Box
      sx={{
        maxWidth: '100%',
        margin: '0',
      }}
    >
      <Tabs
        aria-label='Candidate profile tabs'
        currentTab={currentTab}
        setCurrentTab={handleTabChange}
        tabs={tabItems}
        tabPanelSx={{
          p: 0,
        }}
        muiTabIndicator={{
          maxWidth: '100%',
          width: '29px',
          height: '0.1875rem',
          borderRadius: '6.25rem',
        }}
        sectionName='candidate-profile'
        tabPanelWidth={{ width: '100%' }}
        tabListItemSx={{
          marginRight: '16px',
          '&.MuiButtonBase-root': {
            '& p': {
              fontWeight: '400',
              fontSize: '18px',
              textAlign: 'center',
              fontFamily: 'Lato',
              fontStyle: 'normal',
              lineHeight: '24px',
              color: '#243d42',
              padding: '8px',
            },
          },
          '&.Mui-selected': {
            '& p': {
              color: '#008369',
              fontWeight: '700',
            },
          },
        }}
      />
    </Box>
  );
};

export default CandidateEducationPage;
